import React, {
  useState,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from "react";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { useOauth2Login } from "../../../_utils/hooks";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  Divider,
  Button,
  ModalCloseButton,
  Flex,
} from "@chakra-ui/react";
import {
  GOOGLE_AUTH_URL,
  GOOGLE_CLIENT_ID,
  DOMAIN_NAME,
} from "../../../_actions/endpoints";
import { analyticsTrackEvent } from "../../../_utils/common";
import { AppIdType } from "../../../_utils/constants";
import { YOUTUBE_SCOPES } from "./constants";
import { showToast } from "../../../_utils/showToast";
import { addYouTubeAccount } from "../../../_actions/socialAccountsActions";

const YouTubeConnectApp = forwardRef((props, ref) => {
  const { t } = useTranslation(["planner", "common"]);
  const { onSuccess } = props;
  const [open, setOpen] = useState(false);
  const [tokenLoading, setTokenLoading] = useState(false);
  const dispatch = useDispatch();

  const handleModalClose = () => {
    setOpen(false);
  };

  const onAuthSuccess = async (codeResponse) => {
    setTokenLoading(true);

    try {
      const addResponse = await dispatch(
        addYouTubeAccount({
          code: codeResponse.code,
          callback_url: DOMAIN_NAME,
        })
      );

      if (addResponse.data.status === 1) {
        showToast({
          description: t("connected_youtube"),
          title: t("connected"),
          status: "success",
        });

        handleModalClose();
        onSuccess(addResponse);

        analyticsTrackEvent("connectedAccount", {
          accountType: AppIdType.YOUTUBE_APP,
        });
      }
    } catch (error) {
      // error already handled in action;
    }

    setTokenLoading(false);
  };

  const { requestSignIn, loading: consentLoading } = useOauth2Login({
    id: "youtube-app",
    authorizationUrl: GOOGLE_AUTH_URL,
    clientId: GOOGLE_CLIENT_ID,
    redirectUri: DOMAIN_NAME,
    responseType: "code",
    onSuccess: onAuthSuccess,
    onFailure: (res) => {},
    scope: YOUTUBE_SCOPES.join(" "),
    extraQueryParams: {
      include_granted_scopes: false,
      access_type: "offline",
      prompt: "consent select_account",
    },
  });

  useEffect(() => {
    if (open) {
      requestSignIn();
    }
  }, [open]);

  useImperativeHandle(ref, () => ({
    setOpen,
  }));

  return (
    <Modal isOpen={open} onClose={handleModalClose} isCentered scrollBehavior="inside">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t("connection.connect_youtube")}</ModalHeader>
        <ModalCloseButton />
        <Divider my="1" />
        <ModalBody>
          <Flex w="100%" direction="row" align="center" justify="center">
            <Button
              variant="solid"
              onClick={() => {
                requestSignIn();
              }}
              disabled={consentLoading || tokenLoading}
              isLoading={consentLoading || tokenLoading}
              loadingText={t("connection.authorize_youtube")}
            >
              {t("connection.authorize_youtube")}
            </Button>
          </Flex>
        </ModalBody>
        <Divider my="1" />
        <ModalFooter>
          <Button
            onClick={(e) => {
              handleModalClose();
            }}
            variant="outline"
          >
            {t("cancel", { ns: "common" })}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
});

YouTubeConnectApp.defaultProps = {
  onSuccess: () => {},
};

YouTubeConnectApp.propTypes = {
  onSuccess: PropTypes.func,
};

export default YouTubeConnectApp;
